import {
  For,
  Show,
  createEffect,
  createSignal,
  onCleanup,
} from "solid-js";
import { A, useLocation, useNavigate, useParams } from "@solidjs/router";
import { api } from "../api/client";
import type { CompletedWorkout, PlannedWorkout } from "../api/types";
import BottomSheet from "../components/BottomSheet";
import ExerciseIcon from "../components/ExerciseIcon";
import ExerciseNote from "../components/ExerciseNote";
import RecordBadge from "../components/RecordBadge";
import PageHero from "../components/PageHero";
import Cell from "../components/tg/Cell";
import Section from "../components/tg/Section";
import { t, type MessageKey } from "../i18n";
import { metricParts, rowType } from "../lib/activity";
import { displayPerformedAt } from "../lib/datetime";
import { DURATION_PRESETS, formatDuration } from "../lib/duration";
import { backFallbackFromState, goBack, withFrom } from "../lib/nav";
import {
  repeatCompletedHref,
  repeatPlannedHref,
  todayISO,
} from "../lib/repeatWorkout";
import {
  maybeCelebrateStreakIncrease,
  showSessionCelebration,
} from "../lib/streakCelebration";
import { tgAlert, tgConfirm } from "../lib/telegram";
import { useAuth } from "../stores/auth";

interface Props {
  kind: "completed" | "planned";
}

type Workout = CompletedWorkout | PlannedWorkout;
type LoadState = "loading" | "ready" | "error";

export default function WorkoutDetailPage(props: Props) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const backFallback = () => backFallbackFromState(location.state, "/");
  const [repeatOpen, setRepeatOpen] = createSignal(false);
  const [planDate, setPlanDate] = createSignal(todayISO());
  const [completedDate, setCompletedDate] = createSignal(todayISO());
  const [copyNotes, setCopyNotes] = createSignal(false);

  /** Sheet: mark planned done → optional duration → open completed edit. */
  const [completeOpen, setCompleteOpen] = createSignal(false);
  const [completeDuration, setCompleteDuration] = createSignal<number | null>(
    null,
  );
  const [completing, setCompleting] = createSignal(false);

  const [loadState, setLoadState] = createSignal<LoadState>("loading");
  const [workout, setWorkout] = createSignal<Workout | null>(null);

  createEffect(() => {
    const id = params.id;
    const kind = props.kind;
    if (!id) {
      setWorkout(null);
      setLoadState("error");
      return;
    }

    setLoadState("loading");
    setWorkout(null);

    let cancelled = false;
    (async () => {
      try {
        const w =
          kind === "completed"
            ? await api.getCompleted(id)
            : await api.getPlanned(id);
        if (cancelled) return;
        setWorkout(w);
        setLoadState("ready");
      } catch {
        if (cancelled) return;
        setWorkout(null);
        setLoadState("error");
      }
    })();

    onCleanup(() => {
      cancelled = true;
    });
  });

  function back() {
    if (loadState() === "error" || loadState() === "loading") {
      navigate(backFallback(), { replace: true });
      return;
    }
    goBack(navigate, backFallback());
  }

  async function handleDelete() {
    if (!(await tgConfirm(t("CONFIRM_DELETE")))) return;
    try {
      if (props.kind === "completed") {
        await api.deleteCompleted(params.id!);
        await auth.refreshProfile().catch(() => undefined);
      } else {
        await api.deletePlanned(params.id!);
      }
      navigate(backFallback(), { replace: true });
    } catch (err) {
      await tgAlert(err instanceof Error ? err.message : t("ERROR_GENERIC"));
    }
  }

  function openCompleteSheet() {
    setCompleteDuration(null);
    setCompleteOpen(true);
    setRepeatOpen(false);
  }

  function closeCompleteModal() {
    if (completing()) return;
    setCompleteOpen(false);
  }

  async function confirmComplete() {
    if (completing()) return;
    setCompleting(true);
    try {
      const result = await api.completePlanned(params.id!, {
        durationMinutes: completeDuration(),
      });
      const prevStreak = auth.profile()?.streak ?? 0;
      await auth.refreshProfile().catch(() => undefined);
      const grew = maybeCelebrateStreakIncrease(
        prevStreak,
        auth.profile()?.streak,
      );
      if (!grew) {
        showSessionCelebration(
          completeDuration() ?? 0,
          result.completed.title,
        );
      }
      setCompleteOpen(false);
      navigate(`/workouts/completed/${result.completed.id}/edit`, {
        replace: true,
      });
    } catch (err) {
      await tgAlert(err instanceof Error ? err.message : t("ERROR_GENERIC"));
    } finally {
      setCompleting(false);
    }
  }

  function notesQuery() {
    return copyNotes() ? "&copyNotes=1" : "";
  }

  function goRepeatCompleted() {
    const id = params.id!;
    const base =
      props.kind === "completed"
        ? repeatCompletedHref({ id, date: completedDate() })
        : `/workouts/new/completed?fromPlanned=${id}&date=${completedDate()}`;
    navigate(base + notesQuery());
  }

  function goRepeatPlanned() {
    const id = params.id!;
    const base =
      props.kind === "completed"
        ? repeatPlannedHref({ fromCompletedId: id, date: planDate() })
        : repeatPlannedHref({ fromPlannedId: id, date: planDate() });
    navigate(base + notesQuery());
  }

  const title = () => {
    if (loadState() === "loading") return t("LOADING");
    if (loadState() === "error") return t("WORKOUT_NOT_FOUND");
    return workout()?.title ?? t("WORKOUT_NOT_FOUND");
  };

  return (
    <div>
      <BottomSheet
        open={completeOpen()}
        onClose={closeCompleteModal}
        title={t("COMPLETE_WORKOUT_TITLE")}
      >
        <p class="mb-3 px-1 text-[13px] text-ink-muted">
          {t("COMPLETE_WORKOUT_HINT")}
        </p>
        <div class="tg-section mb-3">
          <div class="px-4 py-3">
            <label class="label">{t("DURATION")}</label>
            <div class="flex items-center gap-2">
              <input
                class="input flex-1 tabular-nums"
                type="number"
                min={1}
                step={1}
                placeholder="—"
                value={completeDuration() ?? ""}
                onInput={(e) => {
                  const v = e.currentTarget.value;
                  if (v === "") {
                    setCompleteDuration(null);
                    return;
                  }
                  const n = Math.round(Number(v));
                  if (Number.isFinite(n) && n >= 1) {
                    setCompleteDuration(n);
                  }
                }}
              />
              <span class="shrink-0 text-[15px] text-ink-muted">
                {t("DURATION_MIN")}
              </span>
            </div>
            <div class="mt-2 flex flex-wrap gap-1.5">
              <For each={[...DURATION_PRESETS]}>
                {(m) => (
                  <button
                    type="button"
                    class={
                      completeDuration() === m
                        ? "tg-chip is-active"
                        : "tg-chip"
                    }
                    onClick={() => setCompleteDuration(m)}
                  >
                    {formatDuration(m) || `${m}`}
                  </button>
                )}
              </For>
            </div>
          </div>
        </div>
        <button
          type="button"
          class="btn-primary w-full"
          disabled={completing()}
          onClick={() => void confirmComplete()}
        >
          {completing() ? t("LOADING") : t("SAVE")}
        </button>
      </BottomSheet>

      <PageHero
        title={title()}
        aside={
          <button type="button" class="btn-ghost px-2" onClick={back}>
            {t("BACK")}
          </button>
        }
      />

      <Show when={loadState() === "loading"}>
        <p class="text-[15px] text-ink-muted">{t("LOADING")}</p>
      </Show>

      <Show when={loadState() === "error"}>
        <Section>
          <div class="px-4 py-4">
            <p class="text-[17px] font-medium text-[var(--destructive)]">
              {t("WORKOUT_NOT_FOUND")}
            </p>
            <p class="mt-1 text-[15px] text-ink-muted">
              {t("WORKOUT_NOT_FOUND_HINT")}
            </p>
          </div>
        </Section>
        <button
          type="button"
          class="btn-primary w-full"
          onClick={() => navigate(backFallback(), { replace: true })}
        >
          {t("BACK")}
        </button>
      </Show>

      <Show when={loadState() === "ready" && workout()}>
        {(w) => (
          <>
            <Section>
              <Cell
                title={
                  props.kind === "completed" && "performedAt" in w()
                    ? new Date(
                        displayPerformedAt(w() as CompletedWorkout),
                      ).toLocaleString()
                    : (() => {
                        const p = w() as PlannedWorkout;
                        return p.startTime
                          ? `${p.plannedFor} · ${p.startTime}`
                          : p.plannedFor;
                      })()
                }
                value={
                  props.kind === "planned"
                    ? (w() as PlannedWorkout).status === "completed" ||
                      (w() as PlannedWorkout).completedWorkoutId
                      ? t("DONE")
                      : (w() as PlannedWorkout).status === "missed"
                        ? t("MISSED")
                        : t("PLANNED")
                    : props.kind === "completed" &&
                        "durationMinutes" in w() &&
                        (w() as CompletedWorkout).durationMinutes
                      ? formatDuration(
                          (w() as CompletedWorkout).durationMinutes,
                        )
                      : undefined
                }
              />
              <Show
                when={
                  props.kind === "planned" &&
                  (w() as PlannedWorkout).status === "missed"
                }
              >
                <div class="px-4 py-3 text-[15px] text-[var(--destructive)]">
                  {t("MISSED_HINT")}
                </div>
              </Show>
              <Show when={w().notes}>
                <div class="px-4 py-3 text-[15px] text-ink-muted">{w().notes}</div>
              </Show>
            </Section>

            <Section header={t("EXERCISES")}>
              <For each={w().exercises}>
                {(ex) => (
                  <div
                    class="tg-cell has-leading"
                    style={{ "align-items": "flex-start" }}
                  >
                    <span class="tg-cell-leading">
                      <ExerciseIcon icon={ex.icon} type={rowType(ex)} />
                    </span>
                    <span class="tg-cell-body">
                      <span class="tg-cell-title">{ex.name}</span>
                      <span class="tg-cell-subtitle">
                        {[
                          ...metricParts(ex),
                          ...(ex.muscleGroups ?? []).map((g) =>
                            t(`MG_${g.toUpperCase()}` as MessageKey),
                          ),
                        ].join(" · ")}
                      </span>
                      <RecordBadge kinds={ex.newRecords} />
                      <ExerciseNote notes={ex.notes} />
                    </span>
                  </div>
                )}
              </For>
            </Section>

            <div class="flex flex-col gap-2 pt-1 pb-6">
              <Show
                when={
                  props.kind === "planned" &&
                  "status" in w() &&
                  (w() as PlannedWorkout).status !== "completed" &&
                  !(w() as PlannedWorkout).completedWorkoutId
                }
              >
                <Show
                  when={(w() as PlannedWorkout).status === "missed"}
                  fallback={
                    <>
                      <button
                        type="button"
                        class="btn-primary w-full"
                        onClick={() =>
                          navigate(
                            `/workouts/active?fromPlanned=${params.id!}`,
                          )
                        }
                      >
                        {t("START_WORKOUT_TIMER")}
                      </button>
                      <button
                        type="button"
                        class="btn-secondary w-full"
                        onClick={openCompleteSheet}
                      >
                        {t("MARK_DONE")}
                      </button>
                    </>
                  }
                >
                  <button
                    type="button"
                    class="btn-primary w-full"
                    onClick={openCompleteSheet}
                  >
                    {t("MARK_DONE")}
                  </button>
                  <button
                    type="button"
                    class="btn-secondary w-full"
                    onClick={() =>
                      navigate(`/workouts/active?fromPlanned=${params.id!}`)
                    }
                  >
                    {t("START_WORKOUT_TIMER")}
                  </button>
                </Show>
              </Show>

              <button
                type="button"
                class="btn-primary w-full"
                onClick={() => setRepeatOpen((v) => !v)}
              >
                ↻ {t("REPEAT")}
              </button>

              <Show when={repeatOpen()}>
                <Section footer={t("COPY_EXERCISE_NOTES_HINT")}>
                  <div class="px-4 py-3 text-[15px] text-ink-muted">
                    {t("REPEAT_CHOOSE")}
                  </div>
                  <label class="tg-cell">
                    <span class="tg-cell-body">
                      <span class="tg-cell-title">{t("COPY_NOTES")}</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={copyNotes()}
                      onChange={(e) => setCopyNotes(e.currentTarget.checked)}
                    />
                  </label>
                  <div class="px-4 py-3 space-y-2">
                    <label class="label">{t("SAVE_AS_COMPLETED")}</label>
                    <input
                      class="input"
                      type="date"
                      value={completedDate()}
                      onInput={(e) => setCompletedDate(e.currentTarget.value)}
                    />
                    <button
                      type="button"
                      class="btn-primary w-full"
                      onClick={goRepeatCompleted}
                    >
                      {t("REPEAT_AS_COMPLETED")}
                    </button>
                  </div>
                  <div class="px-4 py-3 space-y-2">
                    <label class="label">{t("SAVE_AS_PLANNED")}</label>
                    <input
                      class="input"
                      type="date"
                      value={planDate()}
                      onInput={(e) => setPlanDate(e.currentTarget.value)}
                    />
                    <button
                      type="button"
                      class="btn-secondary w-full"
                      onClick={goRepeatPlanned}
                    >
                      {t("REPEAT_AS_PLANNED")}
                    </button>
                  </div>
                </Section>
              </Show>

              <A
                href={`/workouts/${props.kind}/${params.id}/edit`}
                state={withFrom(backFallback())}
                class="btn-secondary w-full text-center"
              >
                {t("EDIT")}
              </A>
              <button type="button" class="btn-danger w-full" onClick={handleDelete}>
                {t("DELETE")}
              </button>
            </div>
          </>
        )}
      </Show>
    </div>
  );
}
